import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { businessesKey, businessKey } from "@/business/query";
import { useBusiness } from "@/core/business";

const QUICKBOOKS_CALLBACK_ERRORS: Record<string, string> = {
  invalid_state: "Connection link expired, try again.",
  business_not_found: "Business not found.",
  exchange_failed: "QuickBooks rejected the connection. Try again.",
  missing_params: "QuickBooks returned an incomplete response. Try again.",
};

export const QuickbooksCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { businessId } = useBusiness();

  useEffect(() => {
    const status = searchParams.get("status");
    const callbackBusinessId = searchParams.get("business_id");
    const targetBusinessId = callbackBusinessId ?? businessId;

    const completeCallback = async () => {
      if (status === "connected") {
        await queryClient.invalidateQueries({ queryKey: businessesKey });

        if (targetBusinessId) {
          await queryClient.invalidateQueries({
            queryKey: businessKey(targetBusinessId),
          });
        }

        toast.success("QuickBooks connected");

        navigate(
          callbackBusinessId
            ? "/accounts-payable"
            : targetBusinessId
              ? `/business-settings/${targetBusinessId}`
              : "/accounts-payable",
          { replace: true },
        );
        return;
      }

      toast.error(
        status
          ? (QUICKBOOKS_CALLBACK_ERRORS[status] ??
              "QuickBooks connection failed. Try again.")
          : "QuickBooks connection failed. Try again.",
      );
      navigate("/accounts-payable", { replace: true });
    };

    void completeCallback();
  }, [businessId, navigate, queryClient, searchParams]);

  return (
    <Stack fullWidth align="center" justify="center" className="h-full p-4">
      <Stack gap="md" align="center">
        <LoadingSpinner size="lg" />
        <Typography variant="subtitleW1" className="text-center">
          Completing your QuickBooks connection...
        </Typography>
        <Typography
          variant="bodyW1"
          className="text-center text-secondary-text"
        >
          We’re syncing your connection and sending you back now.
        </Typography>
      </Stack>
    </Stack>
  );
};
